import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, Observable } from "rxjs";
import { Budget } from "src/app/model/budget/budget";
import { BudgetPlan } from "src/app/model/budget/budget-plan";
import { DocumentationItem } from "src/app/model/documentation-item";
import { IntegratedAdmissionAppointmentPlan } from "src/app/model/integrated-admission/integrated-admission-appointment-plan";
import { MedicalOrderScann } from "src/app/model/medical-order-scann";
import { PracticeRegionSearchReservation } from "src/app/model/practice-region-reservation";
import { ValorizationEdit } from "src/app/model/valorization-edit";
import { ValorizationItemAmount } from "src/app/model/valorization-item";
import { AuthService } from "src/app/services/auth.service";
import { BudgetService } from "src/app/services/budget/budget.service";
import { ConfirmDialogService } from "src/app/services/confirm-dialog.service";
import { IntegratedAdmissionService } from "src/app/services/integrated-admission/integrated-admission.service";
import { NotificationToastService } from "src/app/services/notification-toast.service";
import { PracticeService } from "src/app/services/practice.service";
import { ServiceService } from "src/app/services/service.service";
import { ValorizationService } from "src/app/services/valorization/valorization.service";

@Injectable()
export class BudgetWizardService {

  currentBudget: Budget;
  budgets: Budget[] = [];
  patientId: number;
  numberOfExtractedProcedures: number = 0;
  procedures: PracticeRegionSearchReservation[] = [];

  constructor(
    private authService: AuthService,
    private router: Router,
    private notificationToastService: NotificationToastService,
    private valorizationService: ValorizationService,
    private practiceService: PracticeService,
    private serviceService: ServiceService,
    private budgetService: BudgetService
  ) {}

  _dataBudgetsSubject: BehaviorSubject<Budget[]> = new BehaviorSubject<
    Budget[]
  >([]);

  budgets$: Observable<Budget[]> = this._dataBudgetsSubject.asObservable();

  _dataCurrentBudgetSubject: BehaviorSubject<Budget> =
    new BehaviorSubject<Budget>(null);

  currentBudget$: Observable<Budget> =
    this._dataCurrentBudgetSubject.asObservable();

  _dataCurrentPlanSubject: BehaviorSubject<BudgetPlan> =
    new BehaviorSubject<BudgetPlan>(null);

  currentPlanSubject$: Observable<BudgetPlan> =
    this._dataCurrentPlanSubject.asObservable();

  _dataCurrentValorizationSubject: BehaviorSubject<ValorizationEdit> =
    new BehaviorSubject<ValorizationEdit>(null);

  currentValorizationSubject$: Observable<ValorizationEdit> =
    this._dataCurrentValorizationSubject.asObservable();

  _dataCurrentProceduresSubject: BehaviorSubject<
    PracticeRegionSearchReservation[]
  > = new BehaviorSubject<PracticeRegionSearchReservation[]>([]);

  currentProceduresSubject$: Observable<PracticeRegionSearchReservation[]> =
    this._dataCurrentProceduresSubject.asObservable();

  _dataLoadingValorizationSubject: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  currentLoadingValorizationSubject$: Observable<boolean> =
    this._dataLoadingValorizationSubject.asObservable();

  async init() {
    return await this.addNewBudget();
  }

  canExtrarProceduresByIntents(): boolean {
    return true;
  }


  async addNewBudget() {
    try {
      if (!this.currentBudget || this.isCurrentAppointmentValid()) {
        const newBudget = await this.budgetService.getNew();
        const budget = new Budget();
        this.currentBudget = budget;
        this.currentBudget.Date = new Date(newBudget.Date);
        this.currentBudget.FinalizeDate = new Date(newBudget.FinalizeDate);
        this.currentBudget.BudgetText = newBudget.BudgetText;

        budget.PatientId = this.patientId;
        this.budgets.push(budget);
        this._dataBudgetsSubject.next(this.budgets);
        this._dataCurrentBudgetSubject.next(budget);
        this._dataCurrentValorizationSubject.next(budget.ValorizationEdit);
      }
    } catch (err) {
      console.log(err);
      this.notificationToastService.error(err.error, "Presupuesto");
    }
    return this.currentBudget;
  }

  async setProcedureIdSelected(id: any) {

    const procedure = this.procedures.find((p) => p.PracticeRegionId === id);


    if (procedure) {
      await this.setProcedureSelected(procedure);
    }
  }

  get PatientId(): number {
    return this.patientId;
  }

  setPatient(patientId: number) {
    this.patientId = patientId;
    this.currentBudget.PatientId = patientId;
    this.budgets.forEach((b) => (b.PatientId = patientId));
  }

  async setProcedureSelected(procedure: PracticeRegionSearchReservation) {
    try {
      if (this.currentBudget.Plan.Plan && this.currentBudget.Plan.AliquotTax) {
        const service = await this.serviceService.getServiceById(
          procedure.ServiceId
        );

        if (!service.Service.EquipmentId) {
          this.notificationToastService.error(
            "El servicio no tiene equipo asignado",
            "Presupuesto"
          );
        } else {
          this.currentBudget.EquipmentId = service.Service.EquipmentId;
          this.currentBudget.PhysicianId = service.Service.PhisicianId;
          this.currentBudget.ServiceId = service.Service.Id;
          this.currentBudget.CenterId = this.authService.getCenterId();

          if (this.currentSelectedProducts().indexOf(procedure.Id) === -1) {
            await this.calculateValorization(procedure);
          }
        }
      }
    } catch (err) {
      this.notificationToastService.error(err.error, "Presupuesto");
    }
  }

  setObservation(observation: string, budgetText: string) {
    this.currentBudget.Observation = observation;
    this.currentBudget.BudgetText = budgetText;
  }

  setValorization(valorization: ValorizationEdit): void {
    this.currentBudget.ValorizationEdit = valorization;
  }

  setDocuments(documents: DocumentationItem[]): void {
    this.currentBudget.ValorizationEdit.DocumentationItems = [...documents];
  }

  setScanns(scanns: MedicalOrderScann[]): void {
    this.currentBudget.Scanns = [...scanns];
  }


  setExtractedProcedures(): void {
    this.numberOfExtractedProcedures++;
  }

  async setPlan(plan: IntegratedAdmissionAppointmentPlan) {
    this.numberOfExtractedProcedures  = 0;
    this.currentBudget.Plan = plan;

    this.loadProcedures(plan);

    if (
      plan.AliquotTax &&
      this.currentBudget.ValorizationEdit &&
      this.currentBudget.ValorizationEdit.ValorizationAmount
    ) {
      this.currentBudget.ValorizationEdit.ValorizationAmount.Valorization.AliquotTax =
        plan.AliquotTax;

      this.currentBudget.ValorizationEdit.ValorizationAmount.Plan = plan.Plan;

      this.currentBudget.ValorizationEdit.ValorizationAmount.Entity =
        plan.Entity;

      this.currentBudget.ValorizationEdit.ValorizationAmount.Valorization.PlanId =
        plan.Plan.Id;

      const valorizationEdit =
        await this.valorizationService.recalculateValorizationIntegratedProcedure(
          this.currentBudget.ValorizationEdit,
          new Date(),
          this.currentBudget.EquipmentId
        );

      this.setValorization(valorizationEdit);
      this._dataCurrentValorizationSubject.next(valorizationEdit);
    }
  }

  async setName(name: string) {
    this.currentBudget.Name = name;
  }

  async refreshValorizationItem(item: ValorizationItemAmount) {
    const valorizationEdit =
      await this.valorizationService.refreshItemInValorizationEditIntegratedProcedure(
        this.currentBudget.ValorizationEdit,
        item,
        this.currentBudget.Date,
        this.currentBudget.EquipmentId
      );

    this.setValorization(valorizationEdit);
    this._dataCurrentValorizationSubject.next(valorizationEdit);
  }

  async removeValorizationItem(item: ValorizationItemAmount) {
    if (
      !item.ValorizationItem.MedicalAppointmentProcedure
        .PracticeRegionAditionalId &&
      !item.ValorizationItem.MedicalAppointmentProcedure.SetSupplieId
    ) {
      this.currentBudget.ValorizationEdit.ValorizationAmount.ValorizationItemsAmount =
        this.currentBudget.ValorizationEdit.ValorizationAmount.ValorizationItemsAmount.filter(
          (x) =>
            x.ValorizationItem.MedicalAppointmentProcedure.PracticeRegionId !==
            item.ValorizationItem.MedicalAppointmentProcedure.PracticeRegionId
        );
    } else {
      this.currentBudget.ValorizationEdit.ValorizationAmount.ValorizationItemsAmount =
        this.currentBudget.ValorizationEdit.ValorizationAmount.ValorizationItemsAmount.filter(
          (x) => x !== item
        );
    }

    const valorizationEdit =
      await this.valorizationService.recalculateValorizationIntegratedProcedure(
        this.currentBudget.ValorizationEdit,
        this.currentBudget.Date,
        this.currentBudget.EquipmentId
      );
    this.setValorization(valorizationEdit);
    this._dataCurrentValorizationSubject.next(valorizationEdit);
  }

  async finish() {
    try {
      if (this.isCurrentAppointmentValid()) {
        const group = await this.budgetService.create(this.budgets);

        this.notificationToastService.success(
          "Presupuesto creado correctamente",
          "Presupuesto"
        );

        this.router.navigate(["/budget-report", group]);
      }
    } catch (err) {
      this.notificationToastService.error(err.error, "Presupuesto");
    }
  }

  async setCurrent(budget: Budget) {
    if (this.isCurrentAppointmentValid()) {
      await this.setCurrentNoValidation(budget);
    }
  }

  remove(budget: Budget) {
    this.budgets = this.budgets.filter((a) => a !== budget);
    this._dataBudgetsSubject.next(this.budgets);
    if (this.budgets.length > 0) {
      this.setCurrentNoValidation(this.budgets[0]);
    } else {
      this.init();
    }
  }

  isCurrentAppointmentValid(): boolean {
    if (!this.currentBudget.PatientId || this.currentBudget.PatientId === 0) {
      this.notificationToastService.error(
        "Debe seleccionar un paciente",
        "Presupuesto"
      );
      return false;
    }

    if (!this.currentBudget.Name || this.currentBudget.Name === "") {
      this.notificationToastService.error(
        "Debe ingresar un nombre al presupuesto",
        "Presupuesto"
      );
      return false;
    }

    if (!this.currentBudget.Plan.AliquotTax) {
      this.notificationToastService.error(
        "Debe seleccionar una alícuota",
        "Presupuesto"
      );
      return false;
    }

    if (
      !this.currentBudget.ValorizationEdit ||
      !this.currentBudget.ValorizationEdit.ValorizationAmount ||
      this.currentBudget.ValorizationEdit.ValorizationAmount
        .ValorizationItemsAmount.length === 0
    ) {
      this.notificationToastService.error(
        "Debe seleccionar al menos un procedimiento",
        "Admisión"
      );
      return false;
    }

    return true;
  }

  public async loadProcedures(plan: IntegratedAdmissionAppointmentPlan) {
    if (plan.Plan) {
        this.procedures =
        await this.practiceService.getFilteredPracticeRegionSearchReservation(
          plan.Plan.Id,
          this.currentBudget.PhysicianId,
          this.currentBudget.EquipmentId,
          this.currentBudget.CenterId
        );

      const currentSelectedProducts = this.currentSelectedProducts();

      if (currentSelectedProducts.length > 0) {
        const serviceId = this.currentBudget.ServiceId;
        this.procedures = this.procedures.filter((p) => p.ServiceId === serviceId);

        this.procedures = this.procedures.filter(
          (p) => currentSelectedProducts.indexOf(p.Id) === -1
        );
      }
      this._dataCurrentProceduresSubject.next(this.procedures);
    } else {
      this._dataCurrentProceduresSubject.next([]);
    }
  }

  private currentSelectedProducts(): number[] {
    if (
      this.currentBudget.ValorizationEdit &&
      this.currentBudget.ValorizationEdit.ValorizationAmount &&
      this.currentBudget.ValorizationEdit.ValorizationAmount
        .ValorizationItemsAmount.length > 0
    ) {
      return this.currentBudget.ValorizationEdit.ValorizationAmount.ValorizationItemsAmount.map(
        (v) => v.ValorizationItem.MedicalAppointmentProcedure.PracticeRegionId
      );
    } else {
      return [];
    }
  }

  private async calculateValorization(
    procedure: PracticeRegionSearchReservation
  ) {
    try {
      if (!this.currentBudget.Date) {
        this.notificationToastService.error(
          "Debe ingresar la fecha del turno",
          "Admisión"
        );

        return;
      }

      if (
        !this.currentBudget.ValorizationEdit ||
        !this.currentBudget.ValorizationEdit.ValorizationAmount
      ) {
        const valorization =
          await this.valorizationService.getNewAdmissionValorizationIntegratedProcedure(
            this.currentBudget.Date,
            this.currentBudget.Plan.Plan.Id,
            this.currentBudget.EquipmentId,
            [procedure.Id],
            this.currentBudget.CenterId
          );

        valorization.ValorizationEdit.ValorizationAmount.Valorization.AliquotTax =
          this.currentBudget.Plan.AliquotTax;
        this.setValorization(valorization.ValorizationEdit);
        this._dataCurrentValorizationSubject.next(
          valorization.ValorizationEdit
        );
      } else {
        this.currentBudget.ValorizationEdit.PracticeRegionAdd = procedure;
        const valorizationEdit =
          await this.valorizationService.addItemValorizationIntegratedProcedure(
            this.currentBudget.ValorizationEdit,
            this.currentBudget.Date,
            this.currentBudget.Plan.Plan.Id,
            this.currentBudget.EquipmentId,
            procedure.Id,
            this.currentBudget.CenterId,
            this.currentBudget.PhysicianId
          );

        this.setValorization(valorizationEdit);
        this._dataCurrentValorizationSubject.next(valorizationEdit);
      }
    } catch (err) {
      this.notificationToastService.error(err.error, "Presupuesto");
    } finally {
      await this.revalorizate();
    }
  }

  async revalorizate(affectParticular: boolean = false) {
    try {
      if (
        this.currentBudget.ValorizationEdit &&
        this.currentBudget.ValorizationEdit.ValorizationAmount &&
        this.currentBudget.ValorizationEdit.ValorizationAmount
          .ValorizationItemsAmount
      ) {
        this._dataLoadingValorizationSubject.next(true);
        const valorizationEdit =
          await this.valorizationService.recalculateValorizationIntegratedProcedure(
            this.currentBudget.ValorizationEdit,
            this.currentBudget.Date,
            this.currentBudget.EquipmentId
          );
        this.setValorization(valorizationEdit);
        this._dataCurrentValorizationSubject.next(valorizationEdit);
      }
    } catch (err) {
      console.log(err);
      this.notificationToastService.error(err.error, "Admisión");
    } finally {
      this._dataLoadingValorizationSubject.next(false);
    }
  }

  private async setCurrentNoValidation(budget: Budget) {
    this.currentBudget = budget;
    this._dataCurrentBudgetSubject.next(budget);
    this._dataCurrentPlanSubject.next(budget.Plan);
    this._dataCurrentValorizationSubject.next(budget.ValorizationEdit);
    await this.loadProcedures(budget.Plan);
  }
}
